import React, { useState, useEffect } from "react";
import axios from "axios";
import { Form, Select, InputNumber, Button, Checkbox, message } from 'antd';
import * as FaIcons from 'react-icons/fa';
import { NODE_BACKEND_API_URL } from "../../config";
import { brandVariables as demoBrandVariables } from '../../mocks/data/brands.demo';

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

// Define the IVA options
const ivaOptions = [
    { value: "1.21", label: "21%" },
    { value: "1.11", label: "10.5%" },
    { value: "1.00", label: "Sin IVA (0%)" },
];

function VariableEditor({ onVariableUpdate }) {
    const [form] = Form.useForm();
    const [brandOptions, setBrandOptions] = useState([]);
    const [loadingBrands, setLoadingBrands] = useState(true);
    const [brandError, setBrandError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // API endpoint for fetching brand quantities (which includes brand names)
    const brandQuantitiesUrl = `${NODE_BACKEND_API_URL}/getallbrands`;
    // API endpoint for updating variables
    const updateUrl = `${NODE_BACKEND_API_URL}/updatevariables`;

    // Effect hook to fetch brand names when the component mounts
    useEffect(() => {
        if (isDemoMode) {
            const fetchedBrands = demoBrandVariables
                .filter(b => b.BrandName !== 'DUTTO')
                .map(b => ({ value: b.BrandName, label: b.BrandName }));
            setBrandOptions(fetchedBrands);
            setLoadingBrands(false);
            return;
        }
        axios.get(brandQuantitiesUrl)
            .then(res => {
                // Map the response data to the format needed for select options
                const fetchedBrands = res.data.map(brand => ({
                    value: brand.BrandName,
                    label: brand.BrandName
                }));

                setBrandOptions(fetchedBrands);
                setLoadingBrands(false);
            })
            .catch(er => {
                console.error("Error fetching brand names:", er);
                setBrandError("Error al cargar la lista de marcas.");
                setLoadingBrands(false);
                setBrandOptions([]);
            });
    }, [brandQuantitiesUrl]);

    // Function to handle form submission
    const handleSubmit = async (values) => {
        setSubmitting(true);

        // Construct the data payload to send to the backend
        const payload = {
            brandName: values.brandName,
            discountFactor: values.discountFactor,
            markupFactor: values.markupFactor,
            taxFactor: values.taxFactor,
        };

        try {
            const res = await axios.put(updateUrl, payload);
            console.log("Update successful:", res.data);
            message.success(`Variables Actualizadas para Marca: ${values.brandName}`);

            // Call the callback function to notify the parent
            if (onVariableUpdate) {
                onVariableUpdate(values.brandName);
            }

            // Optional: Reset form after successful submission
            // form.resetFields();
        } catch (error) {
            console.error("Update failed:", error);
            if (error.response) {
                message.error(`Error: ${error.response.data.msg || error.response.statusText}`);
            } else if (error.request) {
                message.error('No se recibió respuesta del servidor. Verifique la conexión.');
            } else {
                message.error(`Error: ${error.message}`);
            }
        } finally {
            setSubmitting(false);
        }
    };

    // Render loading state
    if (loadingBrands) {
        return (
            <div className="editor-position" style={{ position: 'sticky', top: '10px', zIndex: 10 }}>
                <FaIcons.FaCalculator size={42} />
                <h2>EDITOR DE VARIABLES</h2>
                <div className="variable-editor-div">
                    <p>Cargando lista de marcas...</p>
                </div>
            </div>
        );
    }

    if (brandError) {
        return (
            <div className="editor-position" style={{ position: 'sticky', top: '10px', zIndex: 10 }}>
                <FaIcons.FaCalculator size={42} />
                <h2>EDITOR DE VARIABLES</h2>
                <div className="variable-editor-div">
                    <p>{brandError}</p>
                </div>
            </div>
        );
    }

    // Main render function once brands are loaded
    return (
        <div className="editor-position" style={{ position: 'sticky', top: '10px', zIndex: 10 }}>
            <FaIcons.FaCalculator size={42} />
            <h2>EDITOR DE VARIABLES</h2>
            <div className="variable-editor-div">
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{
                        brandName: undefined,
                        taxFactor: undefined,
                        discountFactor: undefined,
                        markupFactor: undefined,
                        confirmUpdate: false,
                    }}
                >
                    <Form.Item
                        label="Marca"
                        name="brandName"
                        rules={[{ required: true, message: 'Por favor, seleccione una marca' }]}
                    >
                        <Select
                            placeholder="Seleccionar Marca"
                            showSearch
                            filterOption={(input, option) =>
                                option.children.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {brandOptions.map(brand => (
                                <Select.Option key={brand.value} value={brand.value}>
                                    {brand.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="IVA"
                        name="taxFactor"
                        rules={[{ required: true, message: 'Por favor, seleccione IVA' }]}
                    >
                        <Select placeholder="Seleccionar IVA">
                            {ivaOptions.map(option => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Descuento"
                        name="discountFactor"
                        rules={[{ required: true, message: 'Por favor, ingrese el descuento' }]}
                        tooltip="Ej: 0.90 para 10% descuento"
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            step={0.01}
                            min={0}
                            max={1}
                            placeholder="Ej: 0.90 para 10% descuento"
                        />
                    </Form.Item>

                    <Form.Item
                        label="Ganancia"
                        name="markupFactor"
                        rules={[{ required: true, message: 'Por favor, ingrese la ganancia' }]}
                        tooltip="Ej: 1.50 para 50% aumento"
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            step={0.01}
                            min={1}
                            placeholder="Ej: 1.50 para 50% aumento"
                        />
                    </Form.Item>

                    <Form.Item
                        name="confirmUpdate"
                        valuePropName="checked"
                        rules={[
                            {
                                validator: (_, value) =>
                                    value ? Promise.resolve() : Promise.reject(new Error('Debe confirmar los cambios')),
                            },
                        ]}
                    >
                        <Checkbox>Confirmar cambios</Checkbox>
                    </Form.Item>

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType={isDemoMode ? 'button' : 'submit'}
                            onClick={isDemoMode ? () => message.info('Actualizar variables — disponible en la versión completa del sistema') : undefined}
                            loading={submitting}
                            block
                            size="large"
                        >
                            ACTUALIZAR VARIABLES DE PROVEEDOR
                        </Button>
                    </Form.Item>
                </Form>
            </div>
        </div>
    );
}

export default VariableEditor;